import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { focusHref } from './ScheduledList.jsx';

import { api } from '../api.js';
import { useDebounced } from '../hooks/useDebounced.js';
import { highlight, snippetAround } from '../util/highlight.jsx';

// T363 — search across every message, not one thread.
//
// The thread box answers "where in this conversation?". This answers "where did
// I see Terraform?" — a question that only makes sense across applications, and
// the reason each hit has to name the application it belongs to.
//
// Includes alerts and account mail, unlike everywhere else in this page: when
// you are looking for a company name you saw once, excluding two thirds of the
// corpus because it is not correspondence is unhelpful. The kind is shown on
// each row instead, so nothing is disguised.

const KIND_LABEL = { alert: 'job alert', account: 'account mail' };

function fmt(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function MailSearch() {
  const [query, setQuery] = useState('');
  const q = useDebounced(query, 250);
  const [hits, setHits] = useState(null);
  // T396 — the real number of matches, which is not the same as how many came
  // back. The line below used to print `hits.length` and call it the match
  // count, so a search for "engineer" said "100 messages match" when 108 did.
  const [matched, setMatched] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [searching, setSearching] = useState(false);

  const run = useCallback(async () => {
    if (!q) {
      setHits(null);
      return;
    }
    setSearching(true);
    try {
      const r = await api.get(`/emails?kind=all&limit=100&q=${encodeURIComponent(q)}`);
      setHits(r.emails ?? []);
      setMatched(r.matched ?? (r.emails?.length ?? 0));
      setTruncated(Boolean(r.truncated));
    } catch {
      setHits([]);
      setMatched(0);
      setTruncated(false);
    } finally {
      setSearching(false);
    }
  }, [q]);

  useEffect(() => {
    run();
  }, [run]);

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium text-brand-500">Search all mail</h2>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search every message — a company, a technology, a name…"
        aria-label="Search all mail"
        className="w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
      />

      {q && hits !== null && (
        <p className="text-xs text-slate-500" role="status" aria-live="polite">
          {searching
            ? 'searching…'
            : truncated
              ? `${matched} messages match “${q}” — showing the ${hits.length} most recent`
              : `${matched} message${matched === 1 ? '' : 's'} match “${q}”`}
        </p>
      )}

      {hits !== null && hits.length > 0 && (
        <ul className="space-y-1.5">
          {hits.map((e) => (
            <li key={e._id} className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2">
              <div className="flex flex-wrap items-baseline gap-x-2">
                {e.application ? (
                  // Straight to the thread it belongs to. A hit you cannot get
                  // back to is half an answer.
                  //
                  // T436 — and for a year it was half an answer anyway: the
                  // link went to the tracker's front page, so "straight to
                  // the thread" meant finding the row again among a
                  // hundred. The deep link has existed since T415.
                  <Link
                    to={focusHref(e.application.id)}
                    className="text-xs font-medium text-brand-400 underline decoration-dotted hover:text-brand-300"
                  >
                    {e.application.company || 'Unknown company'}
                  </Link>
                ) : (
                  <span className="text-xs text-slate-500">not linked to an application</span>
                )}
                {e.application?.title && <span className="truncate text-[11px] text-slate-500">{e.application.title}</span>}
                {KIND_LABEL[e.kind] && (
                  <span className="rounded border border-slate-700 px-1 py-px text-[10px] text-slate-500">
                    {KIND_LABEL[e.kind]}
                  </span>
                )}
                <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-500">{fmt(e.sentAt)}</span>
              </div>
              <p className="mt-1 truncate text-xs text-slate-200">{highlight(e.subject || '(no subject)', q)}</p>
              {e.bodyText && (
                <p className="mt-0.5 text-[11px] leading-relaxed text-slate-400">
                  {highlight(snippetAround(e.bodyText, q), q)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
